import {makeAutoObservable} from "mobx";
import {
    api,
    catchApi,
    ShippingConfigConstruct,
    ShippingConfigDestinationConstruct,
    ShippingConfigProcessConstruct
} from "../../api";
import {
    AccountingConfigurationDto,
    CustomerConfigurationDto,
    FulfilmentMaximumVehiclesPerDeliveryMethodDto,
    ShipmentProcessTypeDto,
    ShipmentReadyConfigurationDto,
    ShipmentTypeDto,
    ShippingConfigurationDto,
    ShippingConfigurationEntryDto,
    ShippingDefaultConfigurationEntryDto,
    ShippingDestinationTypeDto,
    StatisticsConfigurationDto
} from "../../api/cms";
import {equals} from "../../utils";
import {validationStore} from "../ValidationStore";


function defaultShippingConfigConstruct(): ShippingConfigConstruct {
    return Object.keys(ShipmentProcessTypeDto)
        .map(e => e as ShipmentProcessTypeDto)
        .reduce((input, key) => {

            input[key] = Object.keys(ShippingDestinationTypeDto)
                .map(e => e as ShippingDestinationTypeDto)
                .reduce((input2, key2) => {

                    input2[key2] = Object.keys(ShipmentTypeDto)
                        .map(e => e as ShipmentTypeDto)
                        .reduce((input3, key3) => {
                            input3[key3] = {
                                value: undefined,
                                defaultValue: undefined
                            }
                            return input3
                        }, {} as ShippingConfigDestinationConstruct)

                    return input2
                }, {} as ShippingConfigProcessConstruct)

            return input
        }, {} as ShippingConfigConstruct)
}

class CmsConfigurationStore {
    get shipmentReadyConfiguration(): ShipmentReadyConfigurationDto[] {
        return this._shipmentReadyConfiguration;
    }

    set shipmentReadyConfiguration(value: ShipmentReadyConfigurationDto[]) {
        this._shipmentReadyConfiguration = value;
    }

    private get initialDto(): CustomerConfigurationDto | undefined {
        return this._initialDto;
    }

    private set initialDto(value: CustomerConfigurationDto | undefined) {
        this._initialDto = value;
    }

    get shippingConfigurationFormData(): ShippingConfigConstruct {
        return this._shippingConfigurationFormData;
    }

    set shippingConfigurationFormData(value: ShippingConfigConstruct) {
        this._shippingConfigurationFormData = value;
    }

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
    }

    get accountingConfiguration(): AccountingConfigurationDto | undefined {
        return this._accountingConfiguration;
    }

    set accountingConfiguration(value: AccountingConfigurationDto | undefined) {
        this._accountingConfiguration = value;
    }

    get statisticsConfiguration(): StatisticsConfigurationDto | undefined {
        return this._statisticsConfiguration;
    }

    set statisticsConfiguration(value: StatisticsConfigurationDto | undefined) {
        this._statisticsConfiguration = value;
    }

    get shippingConfiguration(): ShippingConfigurationDto | undefined {
        return this._shippingConfiguration;
    }

    set shippingConfiguration(value: ShippingConfigurationDto | undefined) {
        this._shippingConfiguration = value;
    }

    get fulfilmentMaximumVehiclesPerDeliveryMethod(): FulfilmentMaximumVehiclesPerDeliveryMethodDto | undefined {
        return this._fulfilmentMaximumVehiclesPerDeliveryMethod;
    }

    set fulfilmentMaximumVehiclesPerDeliveryMethod(value: FulfilmentMaximumVehiclesPerDeliveryMethodDto | undefined) {
        this._fulfilmentMaximumVehiclesPerDeliveryMethod = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    private set valid(value: boolean) {
        this._valid = value;
    }

    private get data(): CustomerConfigurationDto | undefined {
        return this._data;
    }

    private set data(value: CustomerConfigurationDto | undefined) {
        this._data = value;
    }

    get saving(): boolean {
        return this._saving;
    }

    private set saving(value: boolean) {
        this._saving = value;
    }

    private get version(): number {
        return this._version;
    }

    private set version(value: number) {
        this._version = value;
    }

    get storageWarehouseId(): string | undefined {
        return this._storageWarehouseId;
    }

    set storageWarehouseId(value: string | undefined) {
        this._storageWarehouseId = value;
    }

    get storageCabinets(): string[] | undefined {
        return this._storageCabinets;
    }

    set storageCabinets(value: string[] | undefined) {
        this._storageCabinets = value;
    }

    get customerId(): string | undefined {
        return this._customerId;
    }

    set customerId(value: string | undefined) {
        this._customerId = value;
    }

    get dirty(): boolean {
        const current = this.buildDto()
        return !equals(this.initialDto, current)
    }

    private _customerId: string | undefined
    private _storageCabinets: string[] | undefined
    private _storageWarehouseId: string | undefined
    private _fulfilmentMaximumVehiclesPerDeliveryMethod: FulfilmentMaximumVehiclesPerDeliveryMethodDto | undefined
    private _statisticsConfiguration: StatisticsConfigurationDto | undefined
    private _shippingConfiguration: ShippingConfigurationDto | undefined
    private _accountingConfiguration: AccountingConfigurationDto | undefined
    private _shipmentReadyConfiguration: ShipmentReadyConfigurationDto[] = []
    private _shippingConfigurationFormData: ShippingConfigConstruct = defaultShippingConfigConstruct()
    private _version: number = 0
    private _saving: boolean = false
    private _loading: boolean = false
    private _data: CustomerConfigurationDto | undefined
    private _initialDto: CustomerConfigurationDto | undefined
    private _valid: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    async load() {
        const {customerId} = this
        if (!customerId) {
            return
        }
        this.loading = true
        await this.loadDefaultShippingClientConfiguration()

        const result = await catchApi(async () => await api.cms.api.getConfiguration(customerId))
        const data = result?.data
        if (!data) {
            this.loading = false
            return
        }
        this.data = data
        this.reset()
        this.loading = false
    }

    async loadDefaultShippingClientConfiguration() {
        const result = await catchApi(async () => await api.cms.api.getShippingDefaultConfiguration())
        this.addNextShippingClientDataLayer(result?.data?.entries || [], true)
    }


    async save() {
        const {customerId} = this
        if (!customerId) {
            return
        }
        const dto = this.buildDto()

        this.saving = true

        const result = await catchApi(async () => await api.cms.api.updateConfiguration(customerId, dto))
        const data = result?.data
        if (data) {
            this.data = data
        }
        this.reset()

        this.saving = false
    }

    reset() {
        const {data} = this
        this.version = data?.version || 0
        this.storageCabinets = data?.storageCabinets
        this.storageWarehouseId = data?.storageWarehouseId
        this.fulfilmentMaximumVehiclesPerDeliveryMethod = data?.fulfilmentMaximumVehiclesPerDeliveryMethod
        this.statisticsConfiguration = data?.statisticsConfiguration
        this.accountingConfiguration = data?.accountingConfiguration
        this.shippingConfiguration = data?.shippingConfiguration
        this.fillShippingConfiguration()
        this.shipmentReadyConfiguration = data?.shipmentReadyConfiguration || []

        this.initialDto = this.buildDto()
    }

    private fillShippingConfiguration() {
        this.addNextShippingClientDataLayer(this.data?.shippingConfiguration?.entries || [], false)
    }

    private addNextShippingClientDataLayer(entries: (ShippingConfigurationEntryDto | ShippingDefaultConfigurationEntryDto)[], isDefault: boolean) {
        const {shippingConfigurationFormData} = this
        const configCopy = JSON.parse(JSON.stringify(shippingConfigurationFormData)) as ShippingConfigConstruct
        entries.forEach(entry => {
            const {
                destinationType,
                processType,
                shipmentType,
                shippingClientId
            } = entry
            if (shippingClientId) {
                configCopy[processType][destinationType][shipmentType].value = shippingClientId
                if (isDefault) {
                    configCopy[processType][destinationType][shipmentType].defaultValue = shippingClientId
                }
            }
        });
        this.shippingConfigurationFormData = configCopy
    }

    private buildDto(): CustomerConfigurationDto {
        return {
            version: this.version,
            storageCabinets: this.storageCabinets,
            storageWarehouseId: this.storageWarehouseId,
            fulfilmentMaximumVehiclesPerDeliveryMethod: this.fulfilmentMaximumVehiclesPerDeliveryMethod,
            statisticsConfiguration: this.statisticsConfiguration,
            accountingConfiguration: this.accountingConfiguration,
            shippingConfiguration: this.buildShippingConfigurationDto(),
            shipmentReadyConfiguration: this.shipmentReadyConfiguration
        }
    }

    private buildShippingConfigurationDto(): ShippingConfigurationDto {
        const {shippingConfigurationFormData} = this
        const entries = Object.keys(shippingConfigurationFormData).reduce((buffer, key) => {
            const processType = key as ShipmentProcessTypeDto
            const processConstruct = shippingConfigurationFormData[processType]
            Object.keys(processConstruct).reduce((buffer2, key2) => {
                const destinationType = key2 as ShippingDestinationTypeDto
                const destinationConstruct = processConstruct[destinationType]
                Object.keys(destinationConstruct).reduce((buffer3, key3) => {
                    const shipmentType = key3 as ShipmentTypeDto
                    const shippingClientId = destinationConstruct[shipmentType].value
                    const shippingDefaultClientId = destinationConstruct[shipmentType].defaultValue
                    if (shippingClientId && shippingClientId !== shippingDefaultClientId) {
                        buffer3.push({
                            processType,
                            destinationType,
                            shipmentType,
                            shippingClientId: shippingClientId
                        })
                    }
                    return buffer3
                }, buffer2)
                return buffer2
            }, buffer)
            return buffer
        }, [] as ShippingConfigurationEntryDto[])
        return {
            entries
        }
    }

    async validate() {
        const {customerId} = this
        if (!customerId) {
            return
        }

        this.valid = false
        const dto = this.buildDto()
        await validationStore.validateOnEntity(api.cms.api.validateConfiguration, customerId, dto)

        this.valid = Boolean(validationStore.valid)
    }
}

const cmsConfigurationStore = new CmsConfigurationStore()

export {
    cmsConfigurationStore
}
